import { resolveFlag } from '../services/launchDarkly';

import { sanitizeLogArgs } from './sanitizeLogArgs';

const consoleProxy = new Proxy(console, {
  get: (target, propKey) => {
    if (propKey === 'log' || propKey === 'warn' || propKey === 'error') {
      return (message: string, ...args: any[]) => {
        const stack = new Error().stack?.split('\n')?.[3]?.split('at ')?.[1]?.trim(); // Adjust stack level if needed
        if (stack) {
          return target[propKey](stack, message, ...args);
        } else {
          return target[propKey](message, ...args);
        }
      };
    }

    return target[propKey as keyof typeof console];
  },
});

export function devLog(message: string, ...args: any[]) {
  const enableClientLogs = resolveFlag('enable_client_logs', false);
  if (enableClientLogs) {
    consoleProxy.log(message, ...args);

    // NewRelic JSON.stringifies the payload — raw args may hold DOM nodes
    // with circular React fibers, which would throw and abort the caller.
    const safeArgs = sanitizeLogArgs(args);

    if (window.newrelic) {
      window.newrelic.log(message, {
        level: 'info',
        customAttributes: {
          message,
          args: safeArgs,
          context: 'devLog',
        },
      });
    }
  }
}

export function devError(message: string, ...args: any[]) {
  const enableClientLogs = resolveFlag('enable_client_logs', false);
  if (enableClientLogs) {
    consoleProxy.error(message, ...args);

    const safeArgs = sanitizeLogArgs(args);

    if (window.newrelic) {
      window.newrelic.log(message, {
        level: 'error',
        customAttributes: {
          message,
          args: safeArgs,
          context: 'devError',
        },
      });

      window.newrelic.noticeError(new Error(message), {
        message,
        args: safeArgs,
        context: 'devError',
      });
    }
  }
}

export function devWarn(message: string, ...args: any[]) {
  const enableClientLogs = resolveFlag('enable_client_logs', false);
  if (enableClientLogs) {
    consoleProxy.warn(message, ...args);

    const safeArgs = sanitizeLogArgs(args);

    if (window.newrelic) {
      window.newrelic.log(message, {
        level: 'warn',
        customAttributes: {
          message,
          args: safeArgs,
          context: 'devWarn',
        },
      });
    }
  }
}
